/*!
 * Copyright (c) Microsoft Corporation.
 * All rights reserved. See LICENSE in the project root for license information.
 */

import * as React from 'react';
import Dropzone, { Accept, FileRejection } from 'react-dropzone';
import * as XLSX from 'xlsx';
import { IOrderUploadData } from './order-upload.data';
import { IOrderUploadProps } from './order-upload.props.autogenerated';

export interface IOrderUploadItem {
    manufacturer: string;
    part_number: string;
    qty: number;
}

export interface IOrderUploadExtendedProps<T> extends IOrderUploadProps<T> {
    onUploadItems: (data: IOrderUploadItem[]) => void;
}

export interface IOrderUploadViewProps extends IOrderUploadProps<IOrderUploadData> {
    dropzone: React.ReactNode;
    informationSection: React.ReactNode;
}

interface IOrderUploadState {
    items: IOrderUploadItem[];
    errCount: number;
    fileRejections: string[];
}

/**
 *
 * OrderUpload component
 * @extends {React.PureComponent<IOrderUploadProps<IOrderUploadData>>}
 */
class OrderUpload extends React.PureComponent<IOrderUploadExtendedProps<IOrderUploadData>, IOrderUploadState> {
    constructor(props: IOrderUploadExtendedProps<IOrderUploadData>) {
        super(props);
        this._renderInformationSection = this._renderInformationSection.bind(this);
        this.setState({
            items: [],
            errCount: 0,
            fileRejections: []
        });
        this.isValidFile = this.isValidFile.bind(this);
        this.getFiles = this.getFiles.bind(this);
        this.parseFile = this.parseFile.bind(this);
    }

    private parseFile(file: File) {
        const items = this.state.items || [];
        let errCount = this.state.errCount;
        const reader = new FileReader();
        reader.onload = evt => {
            const bstr = evt.target?.result;
            const wb = XLSX.read(bstr, { type: 'binary' });
            const wsname = wb.SheetNames[0];
            const ws = wb.Sheets[wsname];
            const data = XLSX.utils.sheet_to_json<IOrderUploadItem>(ws, { header: 0 });
            data.forEach(d => {
                const qty = typeof d.qty === 'string' || d.qty - Math.floor(d.qty) !== 0 ? 1 : d.qty;
                if (d.manufacturer && d.part_number) {
                    items.push({ qty: qty, part_number: d.part_number, manufacturer: d.manufacturer });
                } else if (d.part_number) {
                    items.push({ qty: qty, part_number: d.part_number, manufacturer: '' });
                } else {
                    errCount++;
                }
            });
            this.setState(
                {
                    items,
                    errCount
                },
                () => {
                    if (typeof this.props.onUploadItems === 'function') {
                        this.props.onUploadItems(items);
                    }
                }
            );
        };
        reader.readAsBinaryString(file);
    }

    private isValidFile(file: File): boolean {
        this.parseFile(file);
        return file.type === 'text/csv';
    }

    private getFiles<T extends File>(files: T[]) {
        this.setState({ items: [], errCount: 0 });
        files.forEach(file => {
            if (this.isValidFile(file)) {
                console.log('valid!');
            }
        });
    }

    private handleReject(fileRejections: FileRejection[]) {
        const rejections = fileRejections.map(r => `${r.file.name} is not a valid file format`);
        this.setState({ fileRejections: rejections });
    }

    private renderDropzone() {
        const accept: Accept = {
            'text/*': ['.xlsx', '.csv', '.xls']
        };
        const { dropzoneLabel, openFileButtonText } = this.props.resources;
        return (
            <Dropzone
                onDrop={acceptedFiles => this.getFiles(acceptedFiles)}
                noClick={true}
                accept={accept}
                onDropRejected={this.handleReject}
            >
                {({ getRootProps, isDragAccept, isDragReject, open }) => (
                    <section>
                        <div
                            {...getRootProps()}
                            className={`dropzone ${isDragAccept ? 'dropzone__accept' : ''} ${isDragReject ? 'dropzone__reject' : ''}`}
                        >
                            <p>{dropzoneLabel}</p>
                        </div>
                        <div className='dropzone-footer'>
                            <div className='dropzone-footer__info'>{this._renderInformationSection()}</div>
                            <div className='dropzone-footer__button'>
                                <button type={'button'} onClick={open}>
                                    {openFileButtonText}
                                </button>
                            </div>
                        </div>
                    </section>
                )}
            </Dropzone>
        );
    }

    private _renderInformationSection() {
        const { itemCountInfoLabel, rowsFailedInfoLabel } = this.props.resources;
        return (
            <div>
                <p>
                    {itemCountInfoLabel}
                    {this.state?.items?.length}
                </p>
                <p>
                    {rowsFailedInfoLabel}
                    {this.state?.errCount}
                </p>
            </div>
        );
    }

    public render(): JSX.Element | null {
        const viewProps: IOrderUploadViewProps = {
            ...this.props,
            dropzone: this.renderDropzone(),
            informationSection: this._renderInformationSection()
        };
        return this.props.renderView(viewProps);
    }
}

export default OrderUpload;
